/**
 * Subregions whose own language differs from their country's default.
 *
 * Picking Québec and landing in English is the single most jarring thing this
 * feature could do: Canada's default locale is `en-CA`, so before this map a
 * shopper who answered "Priorité aux produits d'ici" in French was handed an
 * English site. Choosing the region and choosing the language are one act here.
 *
 * The bar for an entry is the same as for a subregion flag: the place has its
 * own language and the overwhelming majority actually use it. Québec qualifies.
 * Catalonia and Wales do not get one — `ca-ES` and `cy-UK` are banned-mint
 * locales in this codebase, so there is nothing to point at.
 *
 * WHY THE SPELLINGS ARE LISTED HERE instead of resolved through the admin-1
 * registry: this module is reached from `region-selection.ts`, which
 * `LocationHandoff` imports into BOTH root layouts — i.e. every page. The
 * registry is ~48KB of Natural Earth data and must stay lazily imported. A
 * four-entry alias list costs nothing and covers every spelling that can
 * actually reach storage (canonical code, English and French names, and the
 * legacy StatsCan PRUID).
 */
export interface SubregionLocaleRule {
  /** Locale path to pin when this subregion is chosen. */
  locale: string;
  /** Accepted stored spellings, already normalized (uppercase, unaccented). */
  matches: readonly string[];
}

export const SUBREGION_DEFAULT_LOCALE: Record<string, SubregionLocaleRule> = {
  'CA:QC': { locale: 'fr-CA', matches: ['QC', 'QUEBEC', 'CA10', 'PQ'] },
};

/** Uppercase and strip diacritics so `Québec`, `quebec` and `QC` all compare equal. */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toUpperCase();
}

/**
 * The locale a subregion should pin, or `null` for the (overwhelmingly common)
 * case where the country default already speaks the right language.
 *
 * Callers must still confirm the returned locale is actually deployed before
 * navigating to it — this map knows what Québec speaks, not what we ship.
 */
export function getSubregionLocale(country: string, rawSubregion: string): string | null {
  const key = `${normalize(country)}:`;
  const needle = normalize(rawSubregion);
  if (!needle) return null;

  for (const [entryKey, rule] of Object.entries(SUBREGION_DEFAULT_LOCALE)) {
    if (!entryKey.startsWith(key)) continue;
    const code = entryKey.slice(key.length);
    if (needle === code || rule.matches.includes(needle)) return rule.locale;
  }
  return null;
}
